import React, { useState, useEffect } from 'react';
import { FiUser, FiMail, FiPhone, FiLock, FiActivity, FiAlertCircle } from 'react-icons/fi';
import Card from '../../components/Card/Card';
import Button from '../../components/Button/Button';
import Input from '../../components/Input/Input';
import { useAuth } from '../../context/AuthContext';
import api from '../../api';
import './Profile.css';

const Profile = ({ showAlert }) => {
    const { user, updateProfile } = useAuth();
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        FullName: '',
        Email: '',
        Phone: '',
        CurrentPassword: '',
        NewPassword: ''
    });

    useEffect(() => {
        if (user) {
            setForm(p => ({
                ...p,
                FullName: user.name || '',
                Email: user.email || '',
                Phone: user.phone || ''
            }));
        }
    }, [user]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(p => ({ ...p, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const res = await api.put('/profile.php', form);
            if (res.data.success) {
                // Update local context
                updateProfile(res.data.user);
                showAlert("Profile Updated", "Your personal information has been synchronized.", "success");
                setForm(p => ({ ...p, CurrentPassword: '', NewPassword: '' }));
            }
        } catch (err) {
            showAlert("Update Failed", err.response?.data?.error || "Could not update profile.", "confirm");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="profile-page">
            <div className="page-header">
                <div>
                    <h2 className="page-header__title">My Profile</h2>
                    <p className="page-header__sub">Manage your personal account settings and security</p>
                </div>
            </div>

            <div className="profile-grid">
                <div className="profile-left">
                    <Card padding="md">
                        <div className="profile-user-hero">
                            <div className="profile-avatar-large">
                                {user?.name?.charAt(0) || user?.username?.charAt(0)}
                            </div>
                            <h3 className="profile-user-name">{user?.name || user?.username}</h3>
                            <p className="profile-user-role">{user?.role} Access</p>
                        </div>
                        
                        <div className="profile-quick-info">
                            <div className="q-info-item">
                                <label>Username</label>
                                <span>@{user?.username}</span>
                                <small>(Cannot be changed)</small>
                            </div>
                            <div className="q-info-item">
                                <label>System ID</label>
                                <span>USR#{user?.id}</span>
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="profile-right">
                    <Card padding="md">
                        <Card.Header>
                            <Card.Title>Account Information</Card.Title>
                        </Card.Header>
                        <form onSubmit={handleSubmit} className="profile-form">
                            <div className="form-grid form-grid--2">
                                <Input 
                                    label="Full Name" 
                                    name="FullName"
                                    value={form.FullName} 
                                    onChange={handleChange} 
                                    required 
                                    prefix={<FiUser />}
                                    className="form-grid__span-2"
                                />
                                <Input 
                                    label="Email Address" 
                                    name="Email"
                                    type="email" 
                                    value={form.Email} 
                                    onChange={handleChange} 
                                    required 
                                    prefix={<FiMail />}
                                />
                                <Input 
                                    label="Phone Number" 
                                    name="Phone"
                                    value={form.Phone} 
                                    onChange={handleChange} 
                                    prefix={<FiPhone />}
                                />
                            </div>

                            <hr className="profile-divider" />

                            <div className="security-section">
                                <h4 className="security-title"><FiLock /> Security Update</h4>
                                <p className="security-text">Verify your current password to change account details or set a new password.</p>
                                
                                <div className="form-grid form-grid--2">
                                    <Input 
                                        label="Current Password" 
                                        name="CurrentPassword"
                                        type="password" 
                                        value={form.CurrentPassword} 
                                        onChange={handleChange} 
                                        placeholder="Required to save"
                                        required
                                    />
                                    <Input 
                                        label="New Password (optional)" 
                                        name="NewPassword"
                                        type="password" 
                                        value={form.NewPassword} 
                                        onChange={handleChange} 
                                        placeholder="Leave blank to keep current"
                                    />
                                </div>
                            </div>

                            <div className="profile-form-footer">
                                <Button type="submit" variant="primary" loading={loading} icon={<FiActivity />}>
                                    Save Profile
                                </Button>
                            </div>
                        </form>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default Profile;
